import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Plus,
  Trash2,
  X,
  Calendar,
  Package,
  AlertCircle,
  CheckCircle,
  Clock,
  Loader2,
  Upload,
  CreditCard,
  DollarSign,
  ShoppingBag,
  ExternalLink
} from 'lucide-react';
import { useSelector } from 'react-redux';
import { getCourier, getCourierSetting, getCurrency } from '../state/courierSlice';
import { getUser } from '../state/authSlice';
import { apiConfig } from '../shared/api-config';
import { showErrorToast, showSuccessToast } from '../utils/toast';
import { formatDualCurrency, getCurrencySymbol } from '../utils/currencyUtils';
import { Button, InputField } from '@shared/components';
import { useDarkMode } from '../context/DarkModeContext';
import { ShopForMeRequestApi, ShopForMeItemApi, ShopForMeRequest as ShopForMeRequestModel, ShopForMeItem as ShopForMeItemModel } from '@shared/sdk-v1';

const ShopForMe: React.FC = () => {
  const { isDarkMode } = useDarkMode();
  const navigate = useNavigate();
  const courier = useSelector(getCourier);
  const courierSetting = useSelector(getCourierSetting);
  const currency = useSelector(getCurrency);
  const user = useSelector(getUser);

  const CUSTOMER_PAGE_SIZE = 15;

  // State
  const [requests, setRequests] = useState<ShopForMeRequestModel[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [showNewRequestForm, setShowNewRequestForm] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState<ShopForMeRequestModel | null>(null);
  const [items, setItems] = useState<ShopForMeItemModel[]>([]);
  const [currentItem, setCurrentItem] = useState<ShopForMeItemModel>({
    productUrl: '',
    productName: '',
    description: '',
    quantity: 1,
    estimatedPrice: 0
  });
  const [notes, setNotes] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<string>('bank_transfer');

  const formatAmount = (value: number | null | undefined): string => {
    if (value === null || value === undefined) return '—';
    // Dual JMD<->USD display only applies to JMD/USD couriers (single exchangeRate).
    if (
      courierSetting?.showDualCurrency &&
      (currency === 'JMD' || currency === 'USD')
    ) {
      return formatDualCurrency(value, !!courierSetting?.useJmd, courierSetting?.exchangeRate || 1);
    }
    const prefix = getCurrencySymbol(currency);
    return `${prefix}${value.toFixed(2)}`;
  };

  // Detect store from URL
  const detectStore = (url: string): string => {
    const lowerUrl = url.toLowerCase();
    if (lowerUrl.includes('amazon')) return 'amazon';
    if (lowerUrl.includes('ebay')) return 'ebay';
    if (lowerUrl.includes('aliexpress')) return 'aliexpress';
    if (lowerUrl.includes('temu')) return 'temu';
    if (lowerUrl.includes('alibaba')) return 'alibaba';
    if (lowerUrl.includes('shein')) return 'shein';
    return 'other';
  };

  // Get store badge color
  const getStoreBadgeColor = (store?: string) => {
    switch (store) {
      case 'amazon':
        return 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200';
      case 'ebay':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200';
      case 'aliexpress':
        return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
      case 'temu':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
      case 'alibaba':
        return 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200';
      case 'shein':
        return 'bg-pink-100 text-pink-800 dark:bg-pink-900 dark:text-pink-200';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200';
    }
  };

  // Friendly label map
  const SFM_STATUS_LABELS: Record<string, string> = {
    pending: 'Pending Review',
    quoted: 'Quote Ready',
    confirmed: 'Quote Accepted',
    pending_verification: 'Payment Under Review',
    paid: 'Payment Verified',
    purchased: 'Item Purchased',
    shipped: 'Shipped',
    delivered: 'Delivered',
    cancelled: 'Cancelled',
  };

  const getSfmStatusLabel = (status?: string) => SFM_STATUS_LABELS[status?.toLowerCase() || ''] || status || 'Unknown';

  // Get status badge color
  const getStatusBadge = (status?: string) => {
    const baseClasses = 'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium';
    
    switch (status?.toLowerCase()) {
      case 'pending':
        return `${baseClasses} bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200`;
      case 'quoted':
        return `${baseClasses} bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200`;
      case 'confirmed':
        return `${baseClasses} bg-indigo-100 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-200`;
      case 'pending_verification':
        return `${baseClasses} bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200`;
      case 'paid':
        return `${baseClasses} bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200`;
      case 'purchased':
        return `${baseClasses} bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200`;
      case 'shipped':
        return `${baseClasses} bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200`;
      case 'delivered':
        return `${baseClasses} bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200`;
      case 'cancelled':
        return `${baseClasses} bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200`;
      default:
        return `${baseClasses} bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200`;
    }
  };

  // Fetch requests with pagination
  const fetchRequests = useCallback(async (reset = true) => {
    if (!user?.id || !courier?.id) return;

    if (reset) {
      setIsLoading(true);
      setHasMore(true);
    } else {
      setIsLoadingMore(true);
    }

    try {
      const api = new ShopForMeRequestApi(apiConfig());
      const skip = reset ? 0 : requests.length;
      const filter = JSON.stringify({
        where: { customerId: user.id, courierId: courier.id },
        include: [
          { relation: 'items' },
          { relation: 'linkedPackage', scope: { fields: ['id', 'trackingNo', 'status', 'description'] } }
        ],
        order: 'createdAt DESC',
        limit: CUSTOMER_PAGE_SIZE,
        skip
      });

      const data = await api.shopForMeRequestFind({ filter });
      if (reset) {
        setRequests(data);
      } else {
        setRequests(prev => [...prev, ...data]);
      }
      setHasMore(data.length === CUSTOMER_PAGE_SIZE);
    } catch (error) {
      console.error('Error fetching requests:', error);
      showErrorToast({ title: 'Error', message: 'Failed to load requests' });
    } finally {
      setIsLoading(false);
      setIsLoadingMore(false);
    }
  }, [user?.id, courier?.id, requests.length]);

  useEffect(() => {
    fetchRequests(true);
  }, [user?.id, courier?.id]);

  // Handle item change
  const handleItemChange = (field: keyof ShopForMeItem, value: any) => {
    setCurrentItem(prev => {
      const updated = { ...prev, [field]: value };
      
      // Auto-detect store when URL changes
      if (field === 'productUrl') {
        updated.store = detectStore(value);
      }
      
      return updated;
    });
  };

  // Add item to list
  const handleAddItem = () => {
    if (!currentItem.productUrl.trim()) {
      showErrorToast({ title: 'Error', message: 'Product URL is required' });
      return;
    }

    setItems(prev => [...prev, { ...currentItem }]);
    setCurrentItem({
      productUrl: '',
      productName: '',
      description: '',
      quantity: 1,
      estimatedPrice: 0
    });
    showSuccessToast({ title: 'Success', message: 'Item added' });
  };

  // Remove item from list
  const handleRemoveItem = (index: number) => {
    setItems(prev => prev.filter((_, i) => i !== index));
  };

  // Submit new request
  const handleSubmitRequest = async () => {
    if (items.length === 0) {
      showErrorToast({ title: 'Error', message: 'Please add at least one item' });
      return;
    }

    if (!user?.id || !courier?.id) {
      showErrorToast({ title: 'Error', message: 'User not authenticated' });
      return;
    }

    setIsSubmitting(true);
    try {
      const requestApi = new ShopForMeRequestApi(apiConfig());
      const itemApi = new ShopForMeItemApi(apiConfig());
      
      const requestData = {
        customerId: user.id,
        courierId: courier.id,
        status: 'pending',
        currency,
        notes: notes.trim() || undefined,
        paymentStatus: 'unpaid'
      };

      const createdRequest = await requestApi.shopForMeRequestCreate({ data: requestData });

      // Create items
      for (const item of items) {
        await itemApi.shopForMeItemCreate({
          data: {
            ...item,
            requestId: createdRequest.id,
            status: 'pending'
          }
        });
      }

      showSuccessToast({ title: 'Success', message: 'Request submitted successfully!' });
      setShowNewRequestForm(false);
      setItems([]);
      setNotes('');
      fetchRequests(true);
    } catch (error) {
      console.error('Error submitting request:', error);
      showErrorToast({ title: 'Error', message: 'Failed to submit request' });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Confirm quote
  const handleConfirmQuote = async (requestId: number) => {
    try {
      const api = new ShopForMeRequestApi(apiConfig());
      await api.shopForMeRequestPrototypePatchAttributes({
        id: String(requestId),
        data: { status: 'confirmed', confirmedAt: new Date() } as any
      });

      showSuccessToast({ title: 'Success', message: 'Quote confirmed!' });
      
      // Refresh the request data to show payment section
      const filter = JSON.stringify({
        where: { id: requestId },
        include: [
          { relation: 'items' },
          { relation: 'linkedPackage', scope: { fields: ['id', 'trackingNo', 'status', 'description'] } }
        ]
      });
      const updatedRequests = await api.shopForMeRequestFind({ filter });
      if (updatedRequests.length > 0) {
        setSelectedRequest(updatedRequests[0]);
      }
      
      // Also refresh the main list
      fetchRequests(true);
    } catch (error) {
      console.error('Error confirming quote:', error);
      showErrorToast({ title: 'Error', message: 'Failed to confirm quote' });
    }
  };

  // Decline quote
  const handleDeclineQuote = async (requestId: number) => {
    if (!window.confirm('Are you sure you want to decline this quote?')) return;

    try {
      const api = new ShopForMeRequestApi(apiConfig());
      await api.shopForMeRequestPrototypePatchAttributes({
        id: String(requestId),
        data: { status: 'cancelled' } as any
      });

      showSuccessToast({ title: 'Success', message: 'Quote declined' });
      fetchRequests(true);
      setSelectedRequest(null);
    } catch (error) {
      console.error('Error declining quote:', error);
      showErrorToast({ title: 'Error', message: 'Failed to decline quote' });
    }
  };

  // Upload proof of payment
  const handleUploadProof = async (requestId: number) => {
    if (!uploadFile) {
      showErrorToast({ title: 'Error', message: 'Please select a file' });
      return;
    }

    try {
      const formData = new FormData();
      formData.append('file', uploadFile);

      const config = apiConfig();
      const uploadResponse = await fetch(
        `${config.basePath}/Uploads/shop-proof-${courier?.id}/upload`,
        {
          method: 'POST',
          headers: {
            'Authorization': config.accessToken || '',
          },
          body: formData
        }
      );

      if (!uploadResponse.ok) throw new Error('Failed to upload file');

      const uploadData = await uploadResponse.json();
      const filename = uploadData.result?.files?.file?.[0]?.name;

      const api = new ShopForMeRequestApi(apiConfig());
      await api.shopForMeRequestPrototypePatchAttributes({
        id: String(requestId),
        data: {
          proofOfPayment: filename,
          paymentMethod: selectedPaymentMethod,
          paymentStatus: 'pending_verification',
          status: 'pending_verification'
        } as any
      });

      showSuccessToast({ title: 'Success', message: 'Proof of payment uploaded! Awaiting admin verification.' });
      setUploadFile(null);
      fetchRequests(true);
      setSelectedRequest(null);
    } catch (error) {
      console.error('Error uploading proof:', error);
      showErrorToast({ title: 'Error', message: 'Failed to upload proof of payment' });
    }
  };

  // Calculate quote expiry countdown
  const getQuoteExpiryCountdown = (expiresAt?: Date) => {
    if (!expiresAt) return null;

    const now = new Date();
    const expiry = new Date(expiresAt);
    const diffMs = expiry.getTime() - now.getTime();

    if (diffMs <= 0) return 'Expired';

    const hours = Math.floor(diffMs / (1000 * 60 * 60));
    const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));

    return `${hours}h ${minutes}m remaining`;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50 dark:bg-gray-900">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary dark:text-blue-400 mx-auto" />
          <p className="mt-2 text-gray-500 dark:text-gray-400">Loading requests...</p>
        </div>
      </div>
    );
  }

  // Request Detail View
  if (selectedRequest) {
    const isQuoted = selectedRequest.status === 'quoted';
    const needsPayment = selectedRequest.status === 'confirmed' || selectedRequest.status === 'quoted';
    const isCompleted = selectedRequest.status === 'delivered';
    const isInProgress = ['paid', 'purchased', 'shipped'].includes(selectedRequest.status || '');

    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 pb-20">
        <div className="max-w-4xl mx-auto p-4 space-y-6">
          {/* Header */}
          <div className="flex items-center justify-between">
            <button
              onClick={() => setSelectedRequest(null)}
              className="text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white p-2 min-w-[44px] min-h-[44px] flex items-center justify-center"
            >
              <X className="h-6 w-6" />
            </button>
            <h2 className="text-xl font-bold text-gray-900 dark:text-white">Request Details</h2>
            <div className={getStatusBadge(selectedRequest.status)}>
              {getSfmStatusLabel(selectedRequest.status)}
            </div>
          </div>

          {/* Items List */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-4 space-y-4">
            <h3 className="font-semibold text-gray-900 dark:text-white">Items ({selectedRequest.items?.length || 0})</h3>
            {selectedRequest.items?.map((item, index) => (
              <div key={index} className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 space-y-2">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <p className="font-medium text-gray-900 dark:text-white">{item.productName || 'Item'}</p>
                    <p className="text-sm text-gray-600 dark:text-gray-300 break-all line-clamp-2">{item.productUrl}</p>
                    {item.description && (
                      <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">{item.description}</p>
                    )}
                  </div>
                  {item.store && (
                    <span className={`ml-2 px-2 py-1 rounded-full text-xs font-medium ${getStoreBadgeColor(item.store)}`}>
                      {item.store}
                    </span>
                  )}
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-600 dark:text-gray-300">Qty: {item.quantity}</span>
                  {item.actualPrice ? (
                    <span className="font-semibold text-gray-900 dark:text-white">
                      {formatAmount(item.actualPrice)}
                    </span>
                  ) : null}
                </div>
              </div>
            ))}
          </div>

          {/* Cancel Button for Pending Requests */}
          {selectedRequest.status === 'pending' && (
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-4">
              <button
                onClick={async () => {
                  if (!window.confirm('Are you sure you want to cancel this request?')) return;
                  try {
                    const api = new ShopForMeRequestApi(apiConfig());
                    await api.shopForMeRequestPrototypePatchAttributes({
                      id: String(selectedRequest.id),
                      data: { status: 'cancelled' } as any
                    });
                    showSuccessToast({ title: 'Success', message: 'Request cancelled' });
                    fetchRequests(true);
                    setSelectedRequest(null);
                  } catch (error) {
                    console.error('Error cancelling request:', error);
                    showErrorToast({ title: 'Error', message: 'Failed to cancel request' });
                  }
                }}
                className="w-full bg-red-500 hover:bg-red-600 text-white py-3 rounded-lg font-medium flex items-center justify-center"
              >
                <X className="h-5 w-5 mr-2" />
                Cancel Request
              </button>
            </div>
          )}

          {/* Quote Section */}
          {isQuoted && (
            <div className="bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-800 rounded-lg p-4 space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="font-semibold text-blue-900 dark:text-blue-100">Quote Ready</h3>
                {selectedRequest.quoteExpiresAt && (
                  <span className="text-sm text-blue-700 dark:text-blue-200 flex items-center">
                    <Clock className="h-4 w-4 mr-1" />
                    {getQuoteExpiryCountdown(selectedRequest.quoteExpiresAt)}
                  </span>
                )}
              </div>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-blue-800 dark:text-blue-100">Subtotal:</span>
                  <span className="font-medium text-blue-900 dark:text-blue-50">
                    {formatAmount(selectedRequest.totalAmount)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-blue-800 dark:text-blue-100">Purchasing Fee:</span>
                  <span className="font-medium text-blue-900 dark:text-blue-50">
                    {formatAmount(selectedRequest.purchasingFee)}
                  </span>
                </div>
                <div className="flex justify-between pt-2 border-t border-blue-200 dark:border-blue-700">
                  <span className="font-semibold text-blue-900 dark:text-blue-50">Grand Total:</span>
                  <span className="font-bold text-lg text-blue-900 dark:text-blue-50">
                    {formatAmount(selectedRequest.grandTotal)}
                  </span>
                </div>
              </div>
              <div className="flex gap-3 mt-4">
                <button
                  onClick={() => handleConfirmQuote(selectedRequest.id!)}
                  className="flex-1 bg-green-500 hover:bg-green-600 text-white py-3 rounded-lg font-medium flex items-center justify-center"
                >
                  <CheckCircle className="h-5 w-5 mr-2" />
                  Confirm & Pay
                </button>
                <button
                  onClick={() => handleDeclineQuote(selectedRequest.id!)}
                  className="flex-1 bg-red-500 hover:bg-red-600 text-white py-3 rounded-lg font-medium"
                >
                  Decline
                </button>
              </div>
            </div>
          )}

          {/* Payment Section */}
          {needsPayment && (
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-4 space-y-4">
              <h3 className="font-semibold text-gray-900 dark:text-white">Payment</h3>
              
              <div className="space-y-3">
                {courierSetting?.isStripeApiEnabled && (
                  <button
                    onClick={() => {
                      const apiBase = import.meta.env.VITE_API_BASE_URL || '';
                      window.location.href = `${apiBase}/ShopForMeRequests/make-payment?requestId=${selectedRequest.id}&method=stripe`;
                    }}
                    className="w-full border border-gray-300 dark:border-gray-600 rounded-lg p-3 hover:bg-gray-50 dark:hover:bg-gray-700 text-left"
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-medium text-gray-900 dark:text-white">Pay with Stripe</span>
                      <CreditCard className="h-5 w-5 text-gray-400 dark:text-gray-500" />
                    </div>
                  </button>
                )}
                
                {courierSetting?.isFygaroApiEnabled && (
                  <button
                    onClick={() => {
                      const apiBase = import.meta.env.VITE_API_BASE_URL || '';
                      window.location.href = `${apiBase}/ShopForMeRequests/make-payment?requestId=${selectedRequest.id}&method=fygaro`;
                    }}
                    className="w-full border border-gray-300 dark:border-gray-600 rounded-lg p-3 hover:bg-gray-50 dark:hover:bg-gray-700 text-left"
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-medium text-gray-900 dark:text-white">Pay with Fygaro</span>
                      <CreditCard className="h-5 w-5 text-gray-400 dark:text-gray-500" />
                    </div>
                  </button>
                )}
                
                {courierSetting?.isWipayApiEnabled && (
                  <button
                    onClick={() => {
                      const apiBase = import.meta.env.VITE_API_BASE_URL || '';
                      window.location.href = `${apiBase}/ShopForMeRequests/make-payment?requestId=${selectedRequest.id}&method=wipay`;
                    }}
                    className="w-full border border-gray-300 dark:border-gray-600 rounded-lg p-3 hover:bg-gray-50 dark:hover:bg-gray-700 text-left"
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-medium text-gray-900 dark:text-white">Pay with WiPay</span>
                      <CreditCard className="h-5 w-5 text-gray-400 dark:text-gray-500" />
                    </div>
                  </button>
                )}
                
                <div className="border border-gray-300 dark:border-gray-600 rounded-lg p-3">
                  <label className="flex items-center justify-between cursor-pointer">
                    <span className="font-medium text-gray-900 dark:text-white">Bank Transfer</span>
                    <input
                      type="radio"
                      name="payment"
                      checked={selectedPaymentMethod === 'bank_transfer'}
                      onChange={() => setSelectedPaymentMethod('bank_transfer')}
                      className="h-4 w-4"
                    />
                  </label>
                  
                  {selectedPaymentMethod === 'bank_transfer' && (
                    <div className="mt-4 space-y-3">
                      <label className="block">
                        <span className="text-sm text-gray-700 dark:text-gray-300">Upload Proof of Payment</span>
                        <input
                          type="file"
                          accept="image/*,.pdf"
                          onChange={(e) => setUploadFile(e.target.files?.[0] || null)}
                          className="mt-1 block w-full text-sm text-gray-500 dark:text-gray-400
                            file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0
                            file:text-sm file:font-medium file:bg-primary file:text-white
                            hover:file:bg-primary/90"
                        />
                      </label>
                      {uploadFile && (
                        <button
                          onClick={() => handleUploadProof(selectedRequest.id!)}
                          className="w-full bg-primary hover:bg-primary/90 text-white py-3 rounded-lg font-medium flex items-center justify-center"
                        >
                          <Upload className="h-5 w-5 mr-2" />
                          Submit Payment
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Timeline for in-progress/completed */}
          {(isInProgress || isCompleted) && (
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-4">
              <h3 className="font-semibold text-gray-900 dark:text-white mb-4">Status Timeline</h3>
              <div className="space-y-4">
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <div className="h-8 w-8 rounded-full bg-green-100 dark:bg-green-900/50 flex items-center justify-center">
                      <CheckCircle className="h-5 w-5 text-green-600 dark:text-green-300" />
                    </div>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm font-medium text-gray-900 dark:text-white">Paid</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {selectedRequest.paidAt ? new Date(selectedRequest.paidAt).toLocaleDateString() : 'N/A'}
                    </p>
                  </div>
                </div>
                
                {['purchased', 'shipped', 'delivered'].includes(selectedRequest.status || '') && (
                  <div className="flex items-start">
                    <div className="flex-shrink-0">
                      <div className="h-8 w-8 rounded-full bg-purple-100 dark:bg-purple-900/50 flex items-center justify-center">
                        <ShoppingBag className="h-5 w-5 text-purple-600 dark:text-purple-300" />
                      </div>
                    </div>
                    <div className="ml-3">
                      <p className="text-sm font-medium text-gray-900 dark:text-white">Purchased</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">Items acquired</p>
                    </div>
                  </div>
                )}
                
                {['shipped', 'delivered'].includes(selectedRequest.status || '') && (
                  <div className="flex items-start">
                    <div className="flex-shrink-0">
                      <div className="h-8 w-8 rounded-full bg-orange-100 dark:bg-orange-900/50 flex items-center justify-center">
                        <Package className="h-5 w-5 text-orange-600 dark:text-orange-300" />
                      </div>
                    </div>
                    <div className="ml-3">
                      <p className="text-sm font-medium text-gray-900 dark:text-white">Shipped</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">On the way</p>
                    </div>
                  </div>
                )}
                
                {selectedRequest.status === 'delivered' && (
                  <div className="flex items-start">
                    <div className="flex-shrink-0">
                      <div className="h-8 w-8 rounded-full bg-green-100 dark:bg-green-900/50 flex items-center justify-center">
                        <CheckCircle className="h-5 w-5 text-green-600 dark:text-green-300" />
                      </div>
                    </div>
                    <div className="ml-3">
                      <p className="text-sm font-medium text-gray-900 dark:text-white">Delivered</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">Completed</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Linked Package Tracking */}
          {selectedRequest.linkedPackage && (
            <div className="bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-200 dark:border-indigo-800 rounded-lg p-4">
              <h3 className="font-semibold text-indigo-900 dark:text-indigo-100 flex items-center gap-2 mb-3">
                <Package className="h-5 w-5" />
                Package Tracking
              </h3>
              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-indigo-800 dark:text-indigo-200">Tracking #:</span>
                  <span className="font-mono font-medium text-indigo-900 dark:text-indigo-100">
                    {selectedRequest.linkedPackage.trackingNo}
                  </span>
                </div>
                {selectedRequest.linkedPackage.status && (
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-indigo-800 dark:text-indigo-200">Package Status:</span>
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800 dark:bg-indigo-800 dark:text-indigo-200 capitalize">
                      {selectedRequest.linkedPackage.status}
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  // New Request Form
  if (showNewRequestForm) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 pb-20">
        <div className="max-w-4xl mx-auto p-4 space-y-6">
          {/* Header */}
          <div className="flex items-center justify-between">
            <button
              onClick={() => {
                setShowNewRequestForm(false);
                setItems([]);
                setNotes('');
              }}
              className="text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white p-2 min-w-[44px] min-h-[44px] flex items-center justify-center"
            >
              <X className="h-6 w-6" />
            </button>
            <h2 className="text-xl font-bold text-gray-900 dark:text-white">New Request</h2>
            <div className="w-6" />
          </div>

          {/* Add Item Form */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-4 space-y-4">
            <h3 className="font-semibold text-gray-900 dark:text-white">Add Item</h3>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Product URL <span className="text-red-500">*</span>
              </label>
              <input
                type="url"
                value={currentItem.productUrl}
                onChange={(e) => handleItemChange('productUrl', e.target.value)}
                placeholder="https://..."
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg 
                  bg-white dark:bg-gray-700 text-gray-900 dark:text-white
                  focus:ring-2 focus:ring-primary focus:border-transparent"
              />
              {currentItem.store && currentItem.store !== 'other' && (
                <div className="mt-2">
                  <span className={`inline-block px-2 py-1 rounded-full text-xs font-medium ${getStoreBadgeColor(currentItem.store)}`}>
                    {currentItem.store}
                  </span>
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Product Name
              </label>
              <input
                type="text"
                value={currentItem.productName}
                onChange={(e) => handleItemChange('productName', e.target.value)}
                placeholder="Optional"
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg 
                  bg-white dark:bg-gray-700 text-gray-900 dark:text-white
                  focus:ring-2 focus:ring-primary focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Description (size, color, notes)
              </label>
              <textarea
                value={currentItem.description}
                onChange={(e) => handleItemChange('description', e.target.value)}
                placeholder="Optional"
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg 
                  bg-white dark:bg-gray-700 text-gray-900 dark:text-white
                  focus:ring-2 focus:ring-primary focus:border-transparent"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Quantity
                </label>
                <input
                  type="number"
                  min="1"
                  value={currentItem.quantity}
                  onChange={(e) => handleItemChange('quantity', parseInt(e.target.value) || 1)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg 
                    bg-white dark:bg-gray-700 text-gray-900 dark:text-white
                    focus:ring-2 focus:ring-primary focus:border-transparent"
                />
              </div>

            </div>

            <button
              onClick={handleAddItem}
              className="w-full bg-primary hover:bg-primary/90 text-white py-3 rounded-lg font-medium flex items-center justify-center"
            >
              <Plus className="h-5 w-5 mr-2" />
              Add Item
            </button>
          </div>

          {/* Items List */}
          {items.length > 0 && (
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-4 space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="font-semibold text-gray-900 dark:text-white">Items ({items.length})</h3>
              </div>
              
              {items.map((item, index) => (
                <div key={index} className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <p className="font-medium text-gray-900 dark:text-white">{item.productName || 'Item'}</p>
                      <p className="text-sm text-gray-600 dark:text-gray-300 break-all line-clamp-2">{item.productUrl}</p>
                      {item.description && (
                        <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">{item.description}</p>
                      )}
                      <div className="flex items-center gap-4 mt-2 text-sm">
                        <span className="text-gray-600 dark:text-gray-300">Qty: {item.quantity}</span>
                        {item.store && (
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStoreBadgeColor(item.store)}`}>
                            {item.store}
                          </span>
                        )}
                      </div>
                    </div>
                    <button
                      onClick={() => handleRemoveItem(index)}
                      className="ml-2 text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
                    >
                      <Trash2 className="h-5 w-5" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Notes */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-4">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Additional Notes (Optional)
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Any special instructions..."
              rows={4}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg 
                bg-white dark:bg-gray-700 text-gray-900 dark:text-white
                focus:ring-2 focus:ring-primary focus:border-transparent"
            />
          </div>

          {/* Submit Button */}
          <button
            onClick={handleSubmitRequest}
            disabled={items.length === 0 || isSubmitting}
            className="w-full bg-green-500 hover:bg-green-600 text-white py-4 rounded-lg font-medium 
              flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                Submitting...
              </>
            ) : (
              <>
                <CheckCircle className="h-5 w-5 mr-2" />
                Submit Request
              </>
            )}
          </button>
        </div>
      </div>
    );
  }

  // Main List View
  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 pb-24">
      <div className="max-w-4xl mx-auto p-4 space-y-6">
        {/* Empty State */}
        {requests.length === 0 ? (
          <div className="text-center py-12">
            <ShoppingBag className="mx-auto h-16 w-16 text-gray-400 dark:text-gray-500" />
            <h3 className="mt-4 text-lg font-medium text-gray-900 dark:text-white">No requests yet</h3>
            <p className="mt-2 text-gray-500 dark:text-gray-400">
              Start by creating your first Shop For Me request
            </p>
          </div>
        ) : (
          <>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">My Requests</h2>
            <div className="space-y-4">
              {requests.map((request) => (
                <div
                  key={request.id}
                  onClick={() => setSelectedRequest(request)}
                  className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-4 cursor-pointer 
                    hover:shadow-lg transition-shadow border border-gray-200 dark:border-gray-700"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <Calendar className="h-4 w-4 text-gray-400 dark:text-gray-500" />
                        <span className="text-sm text-gray-500 dark:text-gray-400">
                          {request.createdAt ? new Date(request.createdAt).toLocaleDateString() : 'N/A'}
                        </span>
                        <span className="text-gray-300 dark:text-gray-600">•</span>
                        <Package className="h-4 w-4 text-gray-400 dark:text-gray-500" />
                        <span className="text-sm text-gray-500 dark:text-gray-400">
                          {request.items?.length || 0} items
                        </span>
                      </div>
                      
                      {request.grandTotal && request.grandTotal > 0 && (
                        <p className="text-lg font-semibold text-gray-900 dark:text-white">
                          {formatAmount(request.grandTotal)}
                        </p>
                      )}
                    </div>
                    
                    <div className={getStatusBadge(request.status)}>
                      {getSfmStatusLabel(request.status)}
                    </div>
                  </div>

                  {request.status === 'quoted' && request.quoteExpiresAt && (
                    <div className="mt-3 flex items-center text-sm text-blue-600 dark:text-blue-300">
                      <Clock className="h-4 w-4 mr-1" />
                      {getQuoteExpiryCountdown(request.quoteExpiresAt)}
                    </div>
                  )}
                </div>
              ))}

              {/* Load More */}
              {hasMore && !isLoading && (
                <button
                  onClick={() => fetchRequests(false)}
                  disabled={isLoadingMore}
                  className="w-full py-3 text-sm font-medium text-primary hover:bg-primary/10 rounded-lg transition-colors disabled:opacity-50"
                >
                  {isLoadingMore ? 'Loading...' : 'Load More Requests'}
                </button>
              )}
            </div>
          </>
        )}
      </div>

      {/* Floating Action Button */}
      <button
        onClick={() => setShowNewRequestForm(true)}
        className="fixed bottom-20 right-4 md:bottom-6 md:right-6 bg-primary hover:bg-primary/90 text-white 
          rounded-full p-4 shadow-lg hover:shadow-xl transition-all z-10"
      >
        <Plus className="h-6 w-6" />
      </button>
    </div>
  );
};

export default ShopForMe;
